import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, FileText, Settings, BookOpen, HardHat, Shield, Monitor, Sparkles, Wrench, Truck, UtensilsCrossed } from 'lucide-react';

/**
 * Internal linking component for SEO pillar pages
 * Links to other pillar pages + sector pages + CTA to platform
 */
export default function InternalLinks({ currentPage }) {
  const pillarPages = [
    {
      id: 'veille',
      title: 'Veille marchés publics',
      description: 'Automatisez votre veille des appels d\'offres',
      href: '/veille-marches-publics',
      icon: Search,
      anchor: 'Découvrir la veille marchés publics'
    },
    {
      id: 'trouver',
      title: 'Trouver des appels d\'offres',
      description: 'Sources et méthodes pour identifier les marchés',
      href: '/trouver-appels-offres',
      icon: Search,
      anchor: 'Apprendre à trouver des appels d\'offres'
    },
    {
      id: 'analyse',
      title: 'Analyse DCE',
      description: 'Comprendre et analyser un dossier de consultation',
      href: '/analyse-dce',
      icon: FileText,
      anchor: 'Comprendre l\'analyse d\'un DCE'
    },
    {
      id: 'plateforme',
      title: 'Plateforme appels d\'offres',
      description: 'Découvrez notre solution de veille IA',
      href: '/plateforme-appels-offres',
      icon: Settings,
      anchor: 'Explorer la plateforme appels d\'offres'
    },
    {
      id: 'repondre',
      title: 'Répondre aux marchés publics',
      description: 'Guide complet pour candidater efficacement',
      href: '/repondre-marches-publics',
      icon: BookOpen,
      anchor: 'Maîtriser la réponse aux marchés publics'
    }
  ];

  const sectorPages = [
    {
      id: 'btp',
      title: 'Marchés publics BTP',
      description: 'Appels d\'offres bâtiment et construction',
      href: '/marches-publics-btp',
      icon: HardHat,
      anchor: 'Trouver des marchés BTP'
    },
    {
      id: 'securite',
      title: 'Marchés publics Sécurité',
      description: 'Gardiennage, surveillance, sûreté',
      href: '/marches-publics-securite',
      icon: Shield,
      anchor: 'Trouver des marchés sécurité'
    },
    {
      id: 'informatique',
      title: 'Marchés publics IT',
      description: 'Services numériques et informatique',
      href: '/marches-publics-informatique',
      icon: Monitor,
      anchor: 'Trouver des marchés IT'
    },
    {
      id: 'nettoyage',
      title: 'Marchés publics Nettoyage',
      description: 'Propreté et entretien des locaux',
      href: '/marches-publics-nettoyage',
      icon: Sparkles,
      anchor: 'Trouver des marchés nettoyage'
    },
    {
      id: 'formation',
      title: 'Marchés publics Formation',
      description: 'Formation professionnelle et insertion',
      href: '/marches-publics-formation',
      icon: BookOpen,
      anchor: 'Trouver des marchés formation'
    },
    {
      id: 'maintenance',
      title: 'Marchés publics Maintenance',
      description: 'Entretien et services techniques',
      href: '/marches-publics-maintenance',
      icon: Wrench,
      anchor: 'Trouver des marchés maintenance'
    },
    {
      id: 'transport',
      title: 'Marchés publics Transport',
      description: 'Logistique et mobilité',
      href: '/marches-publics-transport',
      icon: Truck,
      anchor: 'Trouver des marchés transport'
    },
    {
      id: 'restauration',
      title: 'Marchés publics Restauration',
      description: 'Restauration collective et traiteur',
      href: '/marches-publics-restauration',
      icon: UtensilsCrossed,
      anchor: 'Trouver des marchés restauration'
    }
  ];

  // Filter out current page from both lists
  const otherPillarPages = pillarPages.filter(page => page.id !== currentPage);
  const otherSectorPages = sectorPages.filter(page => page.id !== currentPage);

  // Combine and limit to 4 items for display
  const displayPages = [...otherPillarPages.slice(0, 3), ...otherSectorPages.slice(0, 1)];
  
  // If on a sector page, show more pillar pages
  if (['btp', 'securite', 'informatique'].includes(currentPage)) {
    displayPages.length = 0;
    displayPages.push(...otherPillarPages.slice(0, 4));
  }

  return (
    <section className="py-16 bg-slate-50">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
          Aller plus loin dans les marchés publics
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {displayPages.map((page) => (
            <Link
              key={page.id}
              to={page.href}
              className="group bg-white p-6 rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all"
            >
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-violet-200 transition-colors">
                <page.icon className="w-5 h-5 text-violet-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-violet-700 transition-colors">
                {page.title}
              </h3>
              <p className="text-sm text-slate-600 mb-3">
                {page.description}
              </p>
              <span className="text-violet-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                {page.anchor}
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>

        {/* CTA vers la plateforme si on n'est pas déjà sur la page plateforme */}
        {currentPage !== 'plateforme' && (
          <div className="mt-12 text-center">
            <Link
              to="/plateforme-appels-offres"
              className="inline-flex items-center gap-2 bg-violet-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-violet-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Découvrir la plateforme de veille IA
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
